import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';

// Variant metadata stored on each cart line
export interface CartVariant {
  id?: string; // variantId (preferably a stable id)
  size?: string;
  color?: string;
  colorHex?: string;
  priceModifier?: number;
  sku?: string;
}

export interface CartItem {
  // Product identity
  id: string; // productId
  name: string;
  image?: string;
  images?: string[];
  category?: string;

  // Pricing
  basePrice: number; // product base price (without modifier)
  price: number; // final unit price (basePrice + priceModifier)
  priceModifier?: number;

  // Variant selection
  variant?: CartVariant;

  // Quantity
  quantity: number;

  // internal composite key to uniquely identify item by product+variant
  _key: string;
}

interface CartState {
  items: CartItem[];
  total: number;
  totalItems: number;
}

const initialState: CartState = {
  items: [],
  total: 0,
  totalItems: 0,
};

type AddToCartPayload = {
  id: string;
  name: string;
  price: number; // when passed by existing callers, treat as final price if no basePrice provided
  basePrice?: number;
  priceModifier?: number;
  image?: string;
  images?: string[];
  category?: string;
  variant?: CartVariant;
  // Legacy top-level variant props
  size?: string;
  color?: string;
  colorHex?: string;
  sku?: string;
};

type RemoveFromCartPayload =
  | string
  | { id: string; variantId?: string; size?: string; color?: string };

type UpdateQuantityPayload = {
  id: string;
  quantity: number;
  variantId?: string;
  size?: string;
  color?: string;
};

// Build a composite key for product + variant
function buildKey(id: string, variant?: CartVariant): string {
  const variantKey =
    variant?.id ||
    (variant?.size || '') + '::' + (variant?.color || '');
  return variantKey && variantKey !== '::' ? `${id}__${variantKey}` : id;
}

function recomputeTotals(state: CartState) {
  state.total = state.items.reduce((sum, it) => sum + it.price * it.quantity, 0);
  state.totalItems = state.items.reduce((sum, it) => sum + it.quantity, 0);
}

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addToCart: (state, action: PayloadAction<AddToCartPayload>) => {
      const p = action.payload;

      const variant: CartVariant | undefined =
        p.variant ||
        (p.size || p.color
          ? {
              size: p.size,
              color: p.color,
              colorHex: p.colorHex,
              priceModifier: p.priceModifier,
              sku: p.sku,
            }
          : undefined);

      const key = buildKey(p.id, variant);

      const basePrice =
        p.basePrice ??
        (p.priceModifier != null ? Number(p.price) - Number(p.priceModifier) : Number(p.price));

      const finalUnitPrice = Number(basePrice) + Number(variant?.priceModifier ?? p.priceModifier ?? 0);

      const existing = state.items.find((it) => it._key === key);
      if (existing) {
        existing.quantity += 1;
      } else {
        state.items.push({
          id: p.id,
          name: p.name,
          image: p.image || p.images?.[0],
          images: p.images,
          category: p.category,
          basePrice: Number(basePrice),
          price: Number(finalUnitPrice),
          priceModifier: Number(variant?.priceModifier ?? p.priceModifier ?? 0) || 0,
          variant,
          quantity: 1,
          _key: key,
        });
      }
      recomputeTotals(state);
    },

    removeFromCart: (state, action: PayloadAction<RemoveFromCartPayload>) => {
      const payload = action.payload;
      if (typeof payload === 'string') {
        // Legacy: remove all lines for this product id
        state.items = state.items.filter((it) => it.id !== payload);
      } else {
        const key = payload.variantId
          ? `${payload.id}__${payload.variantId}`
          : buildKey(payload.id, { size: payload.size, color: payload.color });
        state.items = state.items.filter((it) => it._key !== key);
      }
      recomputeTotals(state);
    },

    updateQuantity: (state, action: PayloadAction<UpdateQuantityPayload>) => {
      const { id, quantity, variantId, size, color } = action.payload;
      const key = variantId ? `${id}__${variantId}` : buildKey(id, { size, color });
      const item = state.items.find((it) => it._key === key) || state.items.find((it) => it.id === id && !it.variant);
      if (item) {
        item.quantity = Math.max(0, quantity);
        if (item.quantity === 0) {
          state.items = state.items.filter((it) => it._key !== item._key);
        }
      }
      recomputeTotals(state);
    },

    clearCart: (state) => {
      state.items = [];
      recomputeTotals(state);
    },

    setCart: (state, action: PayloadAction<CartItem[]>) => {
      // Normalize keys for lines that might not have _key persisted
      state.items = (action.payload || []).map((it) => ({
        ...it,
        _key: it._key || buildKey(it.id, it.variant),
      }));
      recomputeTotals(state);
    },
  },
});

export const { addToCart, removeFromCart, updateQuantity, clearCart, setCart } = cartSlice.actions;

export const selectCart = (state: RootState) => state.cart;

export default cartSlice.reducer;